// ─────────────────────────────────────────────
//  importDate — date-based scheduling rules for the Daily Assignments page.
//
//  The whole system is date-keyed (assignments.date, daily_boards/{fid}_{date})
//  and rolls over by itself at local midnight: tomorrow's docs simply BECOME
//  today's view, yesterday's stay behind as history. These helpers add the
//  editorial rules on top:
//
//    • TODAY LOCKS AT 06:30 local — the OR day is running; a sheet import
//      landing on it would overwrite live staffing. After the lock, imports
//      DEFAULT to tomorrow; only an administrator may consciously override
//      back onto today.
// ─────────────────────────────────────────────

export const TODAY_LOCK_HOUR = 6;
export const TODAY_LOCK_MINUTE = 30;

// Local YYYY-MM-DD for a Date (same basis as rosterDateKey).
export const dayKeyOf = (d = new Date()) => d.toLocaleDateString("en-CA");

// The day after a YYYY-MM-DD key (local, DST-safe via midday anchor).
export function nextDayKey(dayKey) {
  const [y, m, d] = String(dayKey || "").split("-").map(Number);
  if (!y || !m || !d) return dayKey || "";
  const noon = new Date(y, m - 1, d, 12, 0, 0);
  noon.setDate(noon.getDate() + 1);
  return dayKeyOf(noon);
}

// Is TODAY's board locked against imports right now? (06:30+ local)
export function isTodayLocked(now = new Date()) {
  const h = now.getHours();
  const min = now.getMinutes();
  return h > TODAY_LOCK_HOUR || (h === TODAY_LOCK_HOUR && min >= TODAY_LOCK_MINUTE);
}

// Where an import should land by DEFAULT: today before the 06:30 lock
// (fresh day being staged), tomorrow after it (today is live).
export function defaultImportDate(now = new Date()) {
  return isTodayLocked(now) ? nextDayKey(dayKeyOf(now)) : dayKeyOf(now);
}

// May this import target `targetDate` right now?
//   { ok:true }                          — allowed
//   { ok:false, reason:"locked" }        — today after 06:30, no admin override
export function canImportToDate(targetDate, { now = new Date(), isAdmin = false, adminOverride = false } = {}) {
  const today = dayKeyOf(now);
  if (targetDate !== today) return { ok: true };
  if (!isTodayLocked(now)) return { ok: true };
  if (isAdmin && adminOverride) return { ok: true };
  return { ok: false, reason: "locked" };
}
